var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { BlueprintParserContext, GltfFacetsExtensions } from "./parser/GltfFacetsExtension";
import { modelPool } from "@modules/model";
import { BlueprintParserClient } from "./parser/BlueprintParserClient";
const ORIGIN = location.origin;
/**
 * Blueprints are similar to Prefabs in Unity in the sense that they are geometry with additional functionality (e.g. facets)
 */
class BlueprintLoadingService {
    constructor() {
        this.blueprints = new Map();
        this.gltfLoader = new GLTFLoader();
        this.gltfFacetsExtensions = new GltfFacetsExtensions();
        this.gltfLoader.register((parser) => {
            this.gltfFacetsExtensions.setParser(parser);
            return this.gltfFacetsExtensions;
        });
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath("/lib/draco/");
        this.gltfLoader.setDRACOLoader(dracoLoader);
    }
    parseFile(fileId, parent, loadAssets) {
        return __awaiter(this, void 0, void 0, function* () {
            const blueprint = yield this.load(fileId);
            if (blueprint === undefined)
                return undefined;
            const modelFileId = this.resolveModelFileId(blueprint, fileId);
            if (modelFileId !== undefined) {
                return yield this.parseBlueprintFromFileId(blueprint, modelFileId, parent, loadAssets);
            }
            return yield this.parseBlueprint(blueprint, parent);
        });
    }
    resolveModelFileId(blueprint, requestedBlueprintId) {
        var _a;
        const worldLayouterModelFileId = (_a = blueprint.layouterModel) === null || _a === void 0 ? void 0 : _a.fileId;
        if (typeof worldLayouterModelFileId === "string" && worldLayouterModelFileId.length > 0) {
            return worldLayouterModelFileId;
        }
        if (typeof blueprint.fileId === "string" && blueprint.fileId.length > 0) {
            return blueprint.fileId;
        }
        if ((blueprint.models === undefined || blueprint.models.length === 0) && Array.isArray(blueprint.assets)) {
            return requestedBlueprintId;
        }
        return undefined;
    }
    parseBlueprintFromFileId(blueprint, fileId, parent, loadAssets) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = this.resolveFileIdForGltf(fileId);
            const meshUrl = `@${fileId}.glb`;
            const context = new BlueprintParserContext(meshUrl, parent);
            this.gltfFacetsExtensions.context = context;
            this.gltfFacetsExtensions.loadAssets = loadAssets;
            if (blueprint.assets !== undefined && Array.isArray(blueprint.assets)) {
                for (const asset of blueprint.assets) {
                    if (typeof asset.path !== "string")
                        continue;
                    if (typeof asset.fileId !== "string")
                        continue;
                    const unixPath = asset.path.replace(/(\\\\)|\\/g, "/");
                    context.assets.set(unixPath, { fileId: asset.fileId });
                }
            }
            try {
                yield this.gltfLoader.loadAsync(url);
            }
            catch (error) {
                if (GltfFacetsExtensions.isParseAbortError(error)) {
                    return [];
                }
                throw error;
            }
            return context.modelResults;
        });
    }
    parseBlueprint(blueprint, parent) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof parent === "number") {
                parent = modelPool.getModelOrThrow(parent);
            }
            return yield BlueprintParserClient.parse(blueprint, parent);
        });
    }
    getFileUrl(fileId) {
        if (fileId.startsWith("/")) {
            return `${ORIGIN}${fileId}`;
        }
        return `${ORIGIN}/api/v1/asset/${fileId}/blueprint.json`;
    }
    resolveFileIdForGltf(fileId) {
        if (fileId.startsWith("/")) {
            return `${ORIGIN}${fileId}`;
        }
        return `${ORIGIN}/api/v1/asset/${fileId}/model.*`;
    }
    load(fileId) {
        return __awaiter(this, void 0, void 0, function* () {
            let blueprint = this.blueprints.get(fileId);
            if (blueprint !== undefined)
                return yield blueprint;
            blueprint = this.fetchBlueprint(fileId);
            this.blueprints.set(fileId, blueprint);
            return yield blueprint;
        });
    }
    fetchBlueprint(fileId) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = this.getFileUrl(fileId);
            const controller = new AbortController();
            const response = yield fetch(url, {
                method: "GET",
                signal: controller.signal
            });
            if (!response.ok)
                return undefined;
            return (yield response.json());
        });
    }
}
export const blueprintLoadingService = new BlueprintLoadingService();
